import { EventTrigger, EventType, Finder, UpdaterProfile } from '@ekkogmbh/apisdk';
import { useCallback, useMemo, useState } from 'react';
import { StyledSelectField } from 'src/Common/Components/Forms/StyledSelectField';
import {
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { FinderPicker } from 'src/FinderManagement/FinderPicker';
import { Add, Close, Menu } from '@mui/icons-material';
import { UpdaterProfilePicker } from 'src/UpdaterProfileManagement/UpdaterProfilePicker';
import useEventTrigger, { getDefaultTrigger } from '../Providers/EventTriggerProvider';
import { CenteredColumn } from 'src/Common/Components/Stacks';
import StyledTextItem from 'src/Common/Components/Forms/StyledTextItem';
import { InputAction, InputAdditions } from 'src/Common/Components/Forms/InputAdditions';

/** static helpers */

const prettifyEventTypeString = (type: EventType): string => {
  let [context, name] = type.split('/');

  // when there is no '/'
  if (name === undefined) {
    context = 'internal';
    name = type as string;
  }

  const namePretty = name
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return `[${context}] ${namePretty}`;
};

const triggerTooltipWrap = (triggerElement: React.JSX.Element, trigger: EventTrigger): React.JSX.Element => {
  const propKeys = Object.keys(trigger.properties);
  // dont wrap when no props
  if (propKeys.length === 0) {
    return triggerElement;
  }
  return (
    <Tooltip
      title={
        <Stack direction={'column'}>
          <Typography>{'[properties]'}</Typography>
          {propKeys.map((key: string) => (
            <Stack key={`property-tooltip-${key}`} direction={'row'} spacing={2}>
              <Typography>{`${key}:`}</Typography>
              <Typography>{trigger.properties[key] as string}</Typography>
            </Stack>
          ))}
        </Stack>
      }
    >
      {triggerElement}
    </Tooltip>
  );
};

/** component setup */
interface Props {
  coordinate: string;
  excludeTypes?: EventType[];
  variant?: 'grid' | 'list' | 'styled';
}

const Component = ({ coordinate, excludeTypes, variant }: Props): React.JSX.Element => {
  const { _triggers, setTrigger, addTrigger, removeTrigger } = useEventTrigger();

  const [expandedTriggerIndex, setExpandedTriggerIndex] = useState<number | undefined>();
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | undefined>();
  const [pendingTrigger, setPendingTrigger] = useState<EventTrigger | undefined>();

  const setPendingProperty = useCallback(
    (prop: string, value: string) => {
      if (pendingTrigger === undefined) {
        return;
      }

      pendingTrigger.properties[prop] = value;
      setPendingTrigger({
        type: pendingTrigger.type,
        properties: {
          ...pendingTrigger.properties,
        },
      });
    },
    [pendingTrigger, setPendingTrigger],
  );

  const triggerChip = useCallback(
    (trigger: EventTrigger, index: number) => (
      <Chip
        label={
          <span style={{ position: 'relative' }}>
            {triggerTooltipWrap(
              <Typography
                variant={'overline'}
                color={'textPrimary'}
                align={'center'}
                display={'block'}
                style={{
                  lineHeight: 'normal',
                  fontWeight: 700,
                }}
              >
                {prettifyEventTypeString(trigger.type as EventType)}
              </Typography>,
              trigger,
            )}
          </span>
        }
        variant={'outlined'}
        style={{
          position: 'relative',
          margin: 8,
          height: 48,
          overflow: 'hidden',
        }}
        onClick={(e) => {
          setPopoverAnchor(e.currentTarget);
          setExpandedTriggerIndex(index);
          setPendingTrigger({ ...trigger, properties: { ...trigger.properties } });
        }}
        onDelete={() => {
          removeTrigger(index);
        }}
      />
    ),
    [removeTrigger, setPopoverAnchor, setExpandedTriggerIndex, setPendingTrigger],
  );

  const typeSelect = useMemo(() => {
    const excludedValues = excludeTypes ? excludeTypes.map((ex: EventType) => ex.valueOf()) : [];
    const typeStrings = Object.values(EventType).filter((et: string) => !excludedValues.includes(et));
    const presetTypes = ['dashboard', 'event-rule-action'];

    const selectableValues = (typeStrings as string[]).concat(presetTypes);

    return (
      <div>
        <StyledSelectField
          native
          label={'Event Type'}
          value={pendingTrigger?.type ?? EventType.DEVICE_TRIGGER}
          onChange={(e) => {
            setPendingTrigger(getDefaultTrigger(e.target.value as EventType));
          }}
        >
          {selectableValues.map((et: string) => (
            <option key={et} value={et}>
              {prettifyEventTypeString(et as EventType)}
            </option>
          ))}
        </StyledSelectField>
      </div>
    );
  }, [excludeTypes, pendingTrigger]);

  const propertyForm = useMemo(() => {
    if (pendingTrigger === undefined) {
      return <></>;
    }

    switch (pendingTrigger.type) {
      case EventType.UPDATER_APPLIED:
        return (
          <Stack direction={'column'} spacing={1}>
            <UpdaterProfilePicker
              optional
              coordinate={coordinate ?? ''}
              selected={{
                name: pendingTrigger.properties.updaterProfileName as string,
                coordinate: pendingTrigger.properties.updaterProfileCoordinate as string,
              }}
              onChange={(profile: UpdaterProfile | undefined) => {
                if (profile !== undefined) {
                  setPendingProperty('updaterProfileName', profile.name);
                  setPendingProperty('updaterProfileCoordinate', profile.coordinate);
                  setPendingProperty('updaterProfileType', profile.updateStrategy?.type ?? '');
                } else {
                  setPendingProperty('updaterProfile', '');
                  setPendingProperty('updaterProfileCoordinate', '');
                  setPendingProperty('updaterProfileType', '');
                }
              }}
            />
            <StyledTextField
              label={'Compartment Selector Input'}
              value={pendingTrigger.properties.compartmentSelectorInput}
              onChange={(e) => {
                setPendingProperty('compartmentSelectorInput', e.target.value as string);
              }}
            />
          </Stack>
        );
      case EventType.DEVICE_GEOFENCE:
        return (
          <Stack direction={'column'} spacing={1}>
            <div>
              <StyledSelectField
                native
                label={'Location Tracking Event Type'}
                value={pendingTrigger.properties.locationTrackingEventType}
                onChange={(e) => setPendingProperty('locationTrackingEventType', e.target.value as string)}
              >
                {['entry', 'exit', ''].map((ltet) => (
                  <option key={`${ltet}-option`} value={ltet}>
                    {ltet}
                  </option>
                ))}
              </StyledSelectField>
            </div>
            <StyledTextField
              label={'Fence Name'}
              value={pendingTrigger.properties.fenceName}
              onChange={(e) => setPendingProperty('fenceName', e.target.value as string)}
            />
          </Stack>
        );
      case EventType.COMPARTMENT_FOUND:
        return (
          <Stack direction={'column'} spacing={1}>
            <div>
              <FinderPicker
                optional
                coordinate={coordinate ?? ''}
                selected={{
                  name: pendingTrigger.properties.finderName as string,
                  coordinate: pendingTrigger.properties.finderCoordinate as string,
                }}
                onChange={(finder: Finder | undefined) => {
                  if (finder) {
                    setPendingProperty('finderName', finder.name);
                    setPendingProperty('finderCoordinate', finder.coordinate);
                  } else {
                    setPendingProperty('finderName', '');
                    setPendingProperty('finderCoordinate', '');
                  }
                }}
              />
            </div>
            <StyledTextField
              label={'Input'}
              value={pendingTrigger.properties.input}
              onChange={(e) => {
                setPendingProperty('input', e.target.value as string);
              }}
            />
          </Stack>
        );
      case EventType.GENERIC:
        const { context } = pendingTrigger.properties;

        // remove the key field for internal context keywords
        const hideKey = ['dashboard'].indexOf(context as string) >= 0;
        return (
          <Stack direction={'column'} spacing={1}>
            <StyledTextField
              label={'Context'}
              value={context}
              onChange={(e) => {
                setPendingProperty('context', e.target.value as string);
              }}
            />
            {!hideKey && (
              <StyledTextField
                label={'Key'}
                value={pendingTrigger.properties.key}
                onChange={(e) => {
                  setPendingProperty('key', e.target.value as string);
                }}
              />
            )}
          </Stack>
        );
    }

    // Using a default instance to ensure that unset properties also have an input.
    const typeProperties = Object.keys(getDefaultTrigger(pendingTrigger.type as EventType).properties);

    // quick workaround for device trigger type disabling. @FIXME: come up with a cleaner solution
    const disabledProps = ['deviceTriggerType'];

    return (
      <Stack direction={'column'} spacing={1}>
        {typeProperties.map((propKey: string) => (
          <StyledTextField
            key={`prop-input-${propKey}`}
            label={propKey}
            value={pendingTrigger.properties[propKey] ?? ''}
            onChange={(e) => setPendingProperty(propKey, e.target.value as string)}
            disabled={disabledProps.indexOf(propKey) >= 0}
          />
        ))}
      </Stack>
    );
  }, [pendingTrigger, setPendingProperty]);

  const triggersRender = useMemo(() => {
    switch (variant) {
      case 'styled':
        return (
          <CenteredColumn width={'100%'}>
            {_triggers.map((t, idx) => (
              <InputAdditions key={idx} action={{ icon: Menu, onClick: () => console.log('test') }}>
                <StyledTextItem label={prettifyEventTypeString(t.type as EventType)} />
              </InputAdditions>
            ))}
            <ListItemButton
              alignItems={'center'}
              onClick={() => {
                addTrigger(getDefaultTrigger(EventType.DEVICE_TRIGGER));
              }}
            >
              <ListItemIcon>
                <Add />
              </ListItemIcon>
            </ListItemButton>
          </CenteredColumn>
        );
      case 'list':
        return (
          <List sx={{ xs: { maxHeight: '33vh', overflow: 'auto' }, sm: { maxHeight: '66vh', overflow: 'auto' } }}>
            {_triggers.map((t, idx) => (
              <ListItem key={idx} disableGutters>
                <Stack direction={'row'} width={'100%'}>
                  <ListItemButton
                    sx={{
                      border: 1,
                      borderRadius: 1,
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 1,
                    }}
                    onClick={(e) => {
                      setPopoverAnchor(e.currentTarget);
                      setExpandedTriggerIndex(idx);
                      setPendingTrigger({ ...t, properties: { ...t.properties } });
                    }}
                  >
                    {triggerTooltipWrap(<ListItemText>{prettifyEventTypeString(t.type as EventType)}</ListItemText>, t)}
                  </ListItemButton>
                  <InputAction
                    side={'right'}
                    icon={Close}
                    onClick={() => {
                      removeTrigger(idx);
                    }}
                  />
                </Stack>
              </ListItem>
            ))}
            <Divider sx={{ mb: 1 }} />
            <ListItemButton
              alignItems={'center'}
              onClick={() => {
                addTrigger(getDefaultTrigger(EventType.DEVICE_TRIGGER));
              }}
              sx={{
                border: 1,
                borderRadius: 1,
              }}
            >
              <ListItemIcon>
                <Add />
              </ListItemIcon>
            </ListItemButton>
          </List>
        );
      case 'grid':
      default:
        return (
          <Stack direction={'column'} spacing={1}>
            <Grid container spacing={1} alignItems={'center'}>
              {_triggers.map((t: EventTrigger, idx: number) => (
                <Grid item key={`${idx}-trigger-form-gitem`}>
                  {triggerChip(t, idx)}
                </Grid>
              ))}
              <Grid item>
                <IconButton
                  onClick={(e) => {
                    setPopoverAnchor(e.currentTarget);
                    setExpandedTriggerIndex(undefined);
                    setPendingTrigger(getDefaultTrigger(EventType.DEVICE_TRIGGER));
                  }}
                >
                  <Add />
                </IconButton>
              </Grid>
            </Grid>
          </Stack>
        );
    }
  }, [variant, addTrigger, _triggers, setPopoverAnchor, setExpandedTriggerIndex, setPendingTrigger]);

  const popover = useMemo(
    () => (
      <Popover
        open={popoverAnchor !== undefined}
        anchorEl={popoverAnchor}
        onClose={() => {
          setPopoverAnchor(undefined);
          setExpandedTriggerIndex(undefined);
          setPendingTrigger(undefined);
        }}
      >
        <Stack justifyContent={'center'} alignItems={'stretch'} spacing={1} sx={{ margin: 1 }}>
          {typeSelect}
          {propertyForm}
          <Button
            color={'secondary'}
            onClick={() => {
              if (pendingTrigger !== undefined) {
                if (expandedTriggerIndex !== undefined) {
                  setTrigger(expandedTriggerIndex, pendingTrigger!);
                } else {
                  addTrigger(pendingTrigger);
                }
              }
              setPopoverAnchor(undefined);
              setExpandedTriggerIndex(undefined);
              setPendingTrigger(undefined);
            }}
          >
            {'confirm'}
          </Button>
        </Stack>
      </Popover>
    ),
    [
      pendingTrigger,
      expandedTriggerIndex,
      popoverAnchor,
      typeSelect,
      propertyForm,
      setTrigger,
      addTrigger,
      setPopoverAnchor,
      setPendingTrigger,
      setExpandedTriggerIndex,
    ],
  );

  return (
    <>
      {popover}
      {triggersRender}
    </>
  );
};

export const EventTriggerForm = Component;
